import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { IncidentFlagTag } from '@client/components/incidents/IncidentFlagTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { INCIDENT_FLAG_TAG_LABELS } from '@shared/constants/business/incident-flag';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Flag } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/incidents/flags')({
    staticData: {
        metadata: {
            title: 'Flags',
        },
        ui: {
            icon: Flag,
            title: 'Incident flags',
            subtitle: 'Manage the flags that can be applied to incidents',
            menuDescription: 'Manage the flags that can be applied to incidents',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <IncidentFlagsSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['incidentFlag']['listIncidentFlags'];

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['incidentFlag']['listIncidentFlags']>['rows'][number],
    NonNullable<QueryParams['orderBy']>
>();

function IncidentFlagsSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.incidentFlag.deleteIncidentFlag.useMutation();
    const navigate = useNavigate();

    // Curium Lite (CUR-379): template settings are read-only — mutating affordances hidden; the
    // server refuses the mutations regardless.
    const isLiteOrg = useIsLiteOrg();

    const [input, setInput] = useState<QueryParams>({});

    const { data, isPending, error, refetch } = trpc.incidentFlag.listIncidentFlags.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell({ row }) {
                    return (
                        <IncidentFlagTag
                            data={{
                                name: row.name,
                                type: 'SECONDARY',
                                displayColor: row.displayColor,
                            }}
                        />
                    );
                },
            }),
            columnHelper.accessor('tags', {
                id: 'tags',
                header: 'Tags',
                cell({ row }) {
                    return (
                        <TaggedList items={row.tags} accessor={(tag) => INCIDENT_FLAG_TAG_LABELS[tag]} emptyText="—" />
                    );
                },
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                sortable: 'isActive',
                cell({ row }) {
                    return (
                        <Tag variant="outlined" color={row.isActive ? 'green' : 'default'}>
                            {row.isActive ? 'Active' : 'Disabled'}
                        </Tag>
                    );
                },
            }),
            ...(isLiteOrg
                ? []
                : [
                      columnHelper.actions({
                          cell({ row }) {
                              return (
                                  <Button.Delete
                                      onConfirm={(onSuccess) =>
                                          mutate(
                                              { id: row.id },
                                              {
                                                  onSuccess() {
                                                      onSuccess();
                                                      void trpcUtils.incidentFlag.invalidate();
                                                  },
                                                  onError(error) {
                                                      errorMessage.show(error);
                                                  },
                                              },
                                          )
                                      }
                                  />
                              );
                          },
                      }),
                  ]),
        ],
    });

    return (
        <div>
            <Outlet />
            {error && <InlineQueryError compact className="mb-4" error={error} onRetry={() => void refetch()} />}
            <TableToolbar
                actions={
                    !isLiteOrg && (
                        <Button.Add.Link
                            to="/console/$orgId/configuration/compliance/incidents/flags/add"
                            params={{
                                orgId,
                            }}
                            variant="primary"
                            label="New flag"
                        />
                    )
                }
            />
            {!error && (
                <PrismaTable
                    {...tableProps}
                    onRow={
                        isLiteOrg
                            ? undefined
                            : ({ id }) => ({
                                  className: 'cursor-pointer',
                                  onClick(e) {
                                      navigate(
                                          {
                                              to: '/console/$orgId/configuration/compliance/incidents/flags/edit/$incidentFlagId',
                                              params: {
                                                  orgId,
                                                  incidentFlagId: id,
                                              },
                                          },
                                          e,
                                      );
                                  },
                              })
                    }
                />
            )}
        </div>
    );
}
